import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import AttachmentPanel from "./AttachmentPanel";
import {
  HandCoins, Plus, Search, Download, Check, X, Wallet, Paperclip,
  Trash2, Undo2, AlertTriangle, FileWarning,
} from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { DatePicker, MonthPicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatTanggal } from "../../lib/tanggal";
import { useDebounce } from "../../hooks/useDebounce";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `GET /finance/reimbursement` mengurut created_at desc lalu memotong ke
// `limit` (default 300, lihat reimbursement.py) — halaman ini tidak mengirim
// `limit`, jadi 300 itu batasnya. Query sudah dibatasi per bulan (`periode`),
// tapi tetap didampingi `BatasDaftarNotice` demi jujur kalau satu bulan
// ternyata sangat ramai.
const REIMBURSEMENT_BATAS_SERVER = 300;

const APPROVER_ROLES = ["finance_spv", "finance_ceo", "admin", "superadmin"];
const VOID_ROLES = ["finance_ceo", "admin", "superadmin"];

const STATUS_META = {
  pending: { label: "Menunggu", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Disetujui", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  paid: { label: "Dibayar", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Ditolak", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  void: { label: "Dibatalkan", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const TABS = [
  { key: "", label: "Semua" },
  { key: "pending", label: "Menunggu" },
  { key: "approved", label: "Belum Dibayar" },
  { key: "paid", label: "Dibayar" },
  { key: "rejected", label: "Ditolak" },
];

const emptyForm = {
  tanggal: hariIniISO(),
  employee_id: "",
  kategori_beban_id: "",
  nominal: "",
  no_invoice: "",
  tanpa_invoice: false,
  alasan_tanpa_invoice: "",
  keterangan: "",
};

export default function FinanceReimbursement() {
  const { user, can } = useAuth();
  const [periode, setPeriode] = useState(bulanIniISO());
  const [tab, setTab] = useState("");
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 400);
  const [items, setItems] = useState([]);
  const [sum, setSum] = useState(null);
  const [opts, setOpts] = useState({ beban: [], kas_bank: [], employees: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);       // null = modal tutup
  const [payFor, setPayFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [voidFor, setVoidFor] = useState(null);
  const [attFor, setAttFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const isApprover = APPROVER_ROLES.includes(user?.role);
  const isVoider = VOID_ROLES.includes(user?.role);
  const canCreate = can("finance_reimbursement", "create");

  // Nomor urut request: klik tab / ganti periode beruntun tidak boleh membiarkan
  // respons lama (yang datang belakangan) menimpa tab & periode aktif — pelajaran
  // dari bug tab Utang/Piutang. Hanya respons request TERAKHIR yang boleh
  // menyentuh state, termasuk toast error dan flag loading-nya.
  const reqSeq = useRef(0);
  const load = async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const [l, s] = await Promise.all([
        api.get("/finance/reimbursement", { params: { periode, status: tab || undefined, q: qDebounced || undefined } }),
        api.get("/finance/reimbursement/summary", { params: { periode } }),
      ]);
      if (seq !== reqSeq.current) return;   // respons basi — buang
      setItems(l.data.items || []);
      setSum(s.data);
    } catch (e) {
      if (seq === reqSeq.current) {
        setLoadError(e);
        toast.error(e?.response?.data?.detail || "Gagal memuat data reimbursement");
      }
    } finally { if (seq === reqSeq.current) setLoading(false); }
  };

  useEffect(() => { api.get("/finance/reimbursement/options").then(r => setOpts(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [periode, tab, qDebounced]);

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = useMemo(() => sortRows(items, sortKey, sortDir, {
    kode: (it) => it.code || "",
    claimer: (it) => it.claimer_name || "",
    kategori: (it) => it.kategori_beban_name || "",
    nominal: (it) => it.nominal || 0,
    status: (it) => it.status || "",
  }), [items, sortKey, sortDir]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || okMsg);
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
      return false;
    } finally { setBusy(false); }
  };

  const submitForm = async () => {
    const body = {
      ...form,
      nominal: Number(form.nominal) || 0,
      employee_id: form.employee_id || undefined,
    };
    const ok = await act(() => api.post("/finance/reimbursement", body), "Klaim tercatat");
    if (ok) setForm(null);
  };

  const doPay = async () => {
    const ok = await act(
      () => api.post(`/finance/reimbursement/${payFor.item.id}/pay`, {
        sumber_akun_id: payFor.sumber_akun_id, tanggal: payFor.tanggal, catatan: payFor.catatan,
      }), "Pembayaran tercatat");
    if (ok) setPayFor(null);
  };

  const doReject = async () => {
    const ok = await act(
      () => api.post(`/finance/reimbursement/${rejectFor.item.id}/reject`, { reason: rejectFor.reason }),
      "Klaim ditolak");
    if (ok) setRejectFor(null);
  };

  const doVoid = async () => {
    const ok = await act(
      () => api.post(`/finance/reimbursement/${voidFor.item.id}/void`, { reason: voidFor.reason }),
      "Klaim dibatalkan");
    if (ok) setVoidFor(null);
  };

  const doDelete = async (it) => {
    if (!window.confirm(`Hapus klaim ${it.code} milik ${it.claimer_name}?`)) return;
    await act(() => api.delete(`/finance/reimbursement/${it.id}`), "Klaim dihapus");
  };

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem("hris_token");
      const url = `${API}/finance/reimbursement/export?periode=${periode}${tab ? `&status=${tab}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = `Reimbursement_${periode}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch { toast.error("Gagal mengunduh Excel"); }
  };

  const cards = useMemo(() => {
    const s = sum?.summary || {};
    return [
      { key: "pending", label: "Menunggu Persetujuan", v: s.pending, tone: "text-amber-700" },
      { key: "approved", label: "Disetujui, Belum Dibayar", v: s.approved, tone: "text-sky-700" },
      { key: "paid", label: "Sudah Dibayar", v: s.paid, tone: "text-emerald-700" },
      { key: "rejected", label: "Ditolak", v: s.rejected, tone: "text-rose-700" },
    ];
  }, [sum]);

  const selisihLedger = sum ? Math.round((sum.saldo_utang_ledger - sum.belum_dibayar) * 100) / 100 : 0;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-reimbursement">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Klaim Biaya Karyawan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Reimbursement</h1>
          <p className="text-sm text-slate-600 mt-1">
            Disetujui = tercatat sebagai utang ke karyawan. Dibayar = utang lunas dari kas/bank.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MonthPicker value={periode} onChange={setPeriode}
                 data-testid="rmb-periode" className="w-36" />
          <button onClick={exportXlsx} data-testid="rmb-export"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Excel
          </button>
          {canCreate && (
            <button onClick={() => setForm({ ...emptyForm })} data-testid="rmb-add"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Klaim Baru
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <button key={c.key} onClick={() => setTab(c.key)} data-testid={`rmb-card-${c.key}`}
                  className={`text-left bg-white border rounded-xl p-4 shadow-sm transition ${tab === c.key ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200 hover:border-slate-300"}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className={`font-display text-xl font-bold mt-2 ${c.tone}`}>{idr(c.v?.total || 0)}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{c.v?.count || 0} klaim</p>
          </button>
        ))}
      </div>

      {sum && Math.abs(selisihLedger) > 0.5 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800 flex items-start gap-2"
             data-testid="rmb-ledger-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Saldo akun 2200 di buku besar {idr(sum.saldo_utang_ledger)} tidak sama dengan total klaim disetujui yang belum dibayar {idr(sum.belum_dibayar)}
            {" "}(selisih {idr(selisihLedger)}). Cek Audit Trail — kemungkinan ada jurnal manual ke akun 2200.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`rmb-tab-${t.key || "all"}`}
                      className={`px-3 h-8 rounded-lg text-xs font-medium transition ${tab === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Cari nama / kode / no. invoice" data-testid="rmb-search"
                   className="h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-64" />
          </div>
        </div>

        <BatasDaftarNotice shown={items.length} batas={REIMBURSEMENT_BATAS_SERVER} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <SortTh label="Kode / Tanggal" sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rmb-sort-kode" />
                <SortTh label="Claimer" sortKeyName="claimer" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rmb-sort-claimer" />
                <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rmb-sort-kategori" />
                <th className="px-4 py-2.5 font-semibold">Bukti</th>
                <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rmb-sort-nominal" />
                <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rmb-sort-status" />
                <th className="px-4 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada klaim di periode ini" varian="tabel" kolom={7} data-testid="loading-reimbursement">
              {sortedItems.map(it => {
                const meta = STATUS_META[it.status] || STATUS_META.pending;
                return (
                  <tr key={it.id} data-testid={`rmb-row-${it.id}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                      <p className="text-slate-800">{formatTanggal(it.tanggal)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{it.claimer_name}</p>
                      <p className="text-[11px] text-slate-500">{it.claimer_nik}{it.created_on_behalf ? " · diinput Finance" : ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800">{it.kategori_beban_name}</p>
                      {it.keterangan && <p className="text-[11px] text-slate-500 max-w-[220px] truncate">{it.keterangan}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {it.tanpa_invoice ? (
                        <span title={it.alasan_tanpa_invoice}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                          <FileWarning className="w-3 h-3" /> Tanpa invoice
                        </span>
                      ) : (
                        <span className="font-mono text-[11px] text-slate-700">{it.no_invoice}</span>
                      )}
                      {(it.attachments?.length || 0) > 0 && (
                        <span className="ml-1.5 text-[11px] text-slate-400">📎{it.attachments.length}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(it.nominal)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[11px] px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                      {it.status === "rejected" && it.rejection_reason && (
                        <p className="text-[10px] text-rose-600 mt-0.5 max-w-[160px] truncate" title={it.rejection_reason}>{it.rejection_reason}</p>
                      )}
                      {it.status === "paid" && (
                        <p className="text-[10px] text-slate-500 mt-0.5">{formatTanggal(it.tanggal_bayar)} · {it.sumber_akun_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn testid={`rmb-att-${it.id}`} title="Lampiran nota" onClick={() => setAttFor(it)}>
                          <Paperclip className="w-4 h-4" />
                        </IconBtn>
                        {it.status === "pending" && isApprover && (
                          <>
                            <IconBtn testid={`rmb-approve-${it.id}`} title="Setujui" tone="emerald"
                                     onClick={() => act(() => api.post(`/finance/reimbursement/${it.id}/approve`, {}), "Disetujui")}>
                              <Check className="w-4 h-4" />
                            </IconBtn>
                            <IconBtn testid={`rmb-reject-${it.id}`} title="Tolak" tone="rose"
                                     onClick={() => setRejectFor({ item: it, reason: "" })}>
                              <X className="w-4 h-4" />
                            </IconBtn>
                          </>
                        )}
                        {it.status === "approved" && isApprover && (
                          <IconBtn testid={`rmb-pay-${it.id}`} title="Bayar" tone="emerald"
                                   onClick={() => setPayFor({
                                     item: it, sumber_akun_id: opts.kas_bank[0]?.id || "",
                                     tanggal: hariIniISO(), catatan: "",
                                   })}>
                            <Wallet className="w-4 h-4" />
                          </IconBtn>
                        )}
                        {["approved", "paid"].includes(it.status) && isVoider && (
                          <IconBtn testid={`rmb-void-${it.id}`} title="Batalkan (jurnal balik)" tone="rose"
                                   onClick={() => setVoidFor({ item: it, reason: "" })}>
                            <Undo2 className="w-4 h-4" />
                          </IconBtn>
                        )}
                        {["pending", "rejected"].includes(it.status) && can("finance_reimbursement", "delete") && (
                          <IconBtn testid={`rmb-del-${it.id}`} title="Hapus" tone="rose" onClick={() => doDelete(it)}>
                            <Trash2 className="w-4 h-4" />
                          </IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              </LoadingState>
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <Modal title="Klaim Reimbursement Baru" icon={HandCoins} onClose={() => setForm(null)}
               testid="rmb-modal-form" busy={busy} onSubmit={submitForm} submitLabel="Simpan Klaim"
               // BERBAHAYA: form panjang (karyawan, tanggal, nominal, kategori,
               // invoice/alasan, keterangan) yang menyentuh uang.
               dangerous>
          <Field label="Karyawan yang menalangi">
            <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}
                    data-testid="rmb-f-employee" className={inputCls}>
              <option value="">— Saya sendiri —</option>
              {opts.employees.map(e => <option key={e.id} value={e.id}>{e.nama} ({e.nik})</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal pengeluaran" hint="Bulan ini yang menentukan beban masuk laporan bulan mana">
              <DatePicker value={form.tanggal} onChange={v => setForm(f => ({ ...f, tanggal: v }))}
                     data-testid="rmb-f-tanggal" />
            </Field>
            <Field label="Nominal">
              <InputRupiah value={form.nominal}
                     onChange={n => setForm(f => ({ ...f, nominal: n }))}
                     testid="rmb-f-nominal" className={inputCls} />
            </Field>
          </div>
          <Field label="Kategori beban">
            <select value={form.kategori_beban_id} onChange={e => setForm(f => ({ ...f, kategori_beban_id: e.target.value }))}
                    data-testid="rmb-f-kategori" className={inputCls}>
              <option value="">— Pilih kategori —</option>
              {opts.beban.map(b => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
            </select>
          </Field>
          <Field label="No. invoice">
            <input value={form.no_invoice} disabled={form.tanpa_invoice} placeholder="Nomor nota / struk"
                   onChange={e => setForm(f => ({ ...f, no_invoice: e.target.value }))}
                   data-testid="rmb-f-invoice" className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-400`} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.tanpa_invoice} data-testid="rmb-f-tanpa-invoice"
                   onChange={e => setForm(f => ({ ...f, tanpa_invoice: e.target.checked }))}
                   className="w-4 h-4 rounded border-slate-300" />
            Pengeluaran ini tidak punya invoice
          </label>
          {form.tanpa_invoice && (
            <Field label="Alasan tidak ada invoice" hint="Wajib diisi — jadi catatan permanen untuk auditor">
              <input value={form.alasan_tanpa_invoice} placeholder="Contoh: parkir tanpa struk"
                     onChange={e => setForm(f => ({ ...f, alasan_tanpa_invoice: e.target.value }))}
                     data-testid="rmb-f-alasan" className={inputCls} />
            </Field>
          )}
          <Field label="Keterangan">
            <textarea rows={2} value={form.keterangan} placeholder="Untuk keperluan apa"
                      onChange={e => setForm(f => ({ ...f, keterangan: e.target.value }))}
                      data-testid="rmb-f-ket" className={inputCls} />
          </Field>
          <p className="text-[11px] text-slate-500">Foto nota dilampirkan setelah klaim tersimpan, lewat tombol 📎 di daftar.</p>
        </Modal>
      )}

      {payFor && (
        <Modal title={`Bayar ${payFor.item.claimer_name}`} icon={Wallet} onClose={() => setPayFor(null)}
               testid="rmb-modal-pay" busy={busy} onSubmit={doPay} submitLabel={`Bayar ${idr(payFor.item.nominal)}`}>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
            <p className="font-mono text-[11px] text-slate-500">{payFor.item.code}</p>
            <p className="text-slate-800">{payFor.item.kategori_beban_name} · {idr(payFor.item.nominal)}</p>
          </div>
          <Field label="Sumber dana">
            <select value={payFor.sumber_akun_id} onChange={e => setPayFor(p => ({ ...p, sumber_akun_id: e.target.value }))}
                    data-testid="rmb-pay-akun" className={inputCls}>
              {opts.kas_bank.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </Field>
          <Field label="Tanggal transfer">
            <DatePicker value={payFor.tanggal} onChange={v => setPayFor(p => ({ ...p, tanggal: v }))}
                   data-testid="rmb-pay-tanggal" />
          </Field>
          <Field label="Catatan">
            <input value={payFor.catatan} placeholder="Opsional — no. referensi transfer"
                   onChange={e => setPayFor(p => ({ ...p, catatan: e.target.value }))}
                   data-testid="rmb-pay-catatan" className={inputCls} />
          </Field>
        </Modal>
      )}

      {rejectFor && (
        <Modal title="Tolak Klaim" icon={X} tone="rose" onClose={() => setRejectFor(null)}
               testid="rmb-modal-reject" busy={busy} onSubmit={doReject} submitLabel="Tolak Klaim">
          <p className="text-sm text-slate-600">
            {rejectFor.item.claimer_name} · {idr(rejectFor.item.nominal)} · {rejectFor.item.kategori_beban_name}
          </p>
          <Field label="Alasan penolakan" hint="Dilihat karyawan di portal, jadi tulis yang jelas">
            <textarea rows={3} value={rejectFor.reason} data-testid="rmb-reject-reason"
                      onChange={e => setRejectFor(r => ({ ...r, reason: e.target.value }))} className={inputCls} />
          </Field>
        </Modal>
      )}

      {voidFor && (
        <Modal title="Batalkan Klaim" icon={Undo2} tone="rose" onClose={() => setVoidFor(null)}
               testid="rmb-modal-void" busy={busy} onSubmit={doVoid} submitLabel="Buat Jurnal Balik">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            Jurnal lama tidak dihapus. Sistem membuat jurnal kebalikannya bertanggal hari ini,
            sehingga jejak audit tetap utuh dan saldo kembali seperti sebelum klaim ini.
          </div>
          <p className="text-sm text-slate-600">
            {voidFor.item.claimer_name} · {idr(voidFor.item.nominal)} · status {STATUS_META[voidFor.item.status]?.label}
          </p>
          <Field label="Alasan pembatalan">
            <textarea rows={3} value={voidFor.reason} data-testid="rmb-void-reason"
                      onChange={e => setVoidFor(v => ({ ...v, reason: e.target.value }))} className={inputCls} />
          </Field>
        </Modal>
      )}

      {attFor && (
        <AttachmentPanel module="reimbursement" objId={attFor.id}
                         objLabel={`${attFor.code} · ${attFor.claimer_name} · ${idr(attFor.nominal)}`}
                         onClose={() => { setAttFor(null); load(); }} />
      )}
    </div>
  );
}

const inputCls = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, tone }) {
  const cls = tone === "emerald" ? "hover:bg-emerald-50 text-emerald-600"
    : tone === "rose" ? "hover:bg-rose-50 text-rose-600"
      : "hover:bg-slate-100 text-slate-600";
  return (
    <button onClick={onClick} title={title} data-testid={testid}
            className={`w-8 h-8 rounded grid place-items-center transition ${cls}`}>
      {children}
    </button>
  );
}

// `dangerous` opsional (default false = tutup langsung, sama seperti
// sebelumnya) — pasang HANYA pada modal yang formnya panjang atau menyentuh
// nominal yang diketik staf sendiri. Lihat docstring useModalGuard.jsx.
function Modal({ title, icon: Icon, tone, children, onClose, onSubmit, submitLabel, busy, testid, dangerous }) {
  const guard = useModalGuard({ onClose, onSubmit, busy, dangerous });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid={testid}
           className="bg-white rounded-2xl max-w-md w-full max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 ${tone === "rose" ? "text-rose-600" : "text-emerald-600"}`} />} {title}
          </h3>
          <button onClick={guard.requestClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid={testid ? `${testid}-guard` : "modal-guard-bar"} />
        )}
        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
          <button onClick={onSubmit} disabled={busy} data-testid={`${testid}-submit`}
                  className={`h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${tone === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
            {busy ? "Memproses..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
